<template>
	<div class="flex flex-col justify-between mt-4">
		<div v-if="basket?.Items?.length" class="flex flex-col gap-8">
			<template v-for="item in itemsRef" :key="item.Id">
				<SharedProductOrderItem
					:line-item="item"
					:order-id="orderId"
					item-type="product-minicart"
				/>
			</template>
			<div class="flex flex-col px-6 pb-6 gap-8">
				<SharedCheckbox
					id="agree"
					v-model="hasDiscountCode"
					:content="$t('Add discount code')"
				/>
				<div v-if="hasDiscountCode" class="flex items-center gap-2">
					<div class="relative flex-1">
						<TrashIcon
							class="shrink-0 cursor-pointer ml-auto h-6 w-6 absolute right-1.5 top-1.5 z-60"
							@click="handleDiscountCode('delete')"
						/>
						<input
							v-model="discountCode"
							name="discount code"
							type="text"
							class="appearance-none relative block w-full px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-indigo-500 focus:z-10 sm:text-sm"
							@keyup.enter="handleDiscountCode('apply')"
						/>
					</div>
					<button
						type="button"
						class="py-2 px-3 text-white bg-black hover:bg-gray-800 border border-transparent text-sm"
						@click="handleDiscountCode('apply')"
					>
						{{ $t("Apply") }}
					</button>
				</div>
				<div class="flex flex-col gap-2">
					<div class="flex flex-col border-b border-gray-dark">
						<div class="flex justify-between">
							<span>{{ $t("Subtotal") }}</span>
							<span>{{ formatPrice(originalSubtotal) }}</span>
						</div>
						<div
							v-for="promotion in appliedPromotion"
							:key="promotion.Id"
							class="flex justify-between"
						>
							<span class="italic">{{
								promotion.DiscountCode
									? `${$t("Discount code")} (${promotion.DiscountCode})`
									: promotion.Name || $t("Discount")
							}}</span>
							<span class="italic"
								>-{{ formatPrice(promotion?.AppliedAmountIncVat || 0) }}</span
							>
						</div>
						<div class="flex justify-between">
							<span>{{ $t("Shipping") }}</span>
							<span>{{
								formatPrice(basket.Summary?.Freigt.AmountIncVat)
							}}</span>
						</div>
					</div>
					<div class="flex flex-col">
						<div class="flex justify-between">
							<span>{{ $t("Order total") }}</span>
							<span>{{ formatPrice(basket.Summary?.Total.AmountIncVat) }}</span>
						</div>
						<div class="flex justify-between">
							<span>{{ $t("Whereof VAT") }}</span>
							<span>{{ formatPrice(basket.Summary?.Total.Vat) }}</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-else class="flex flex-col gap-8">
			<span class="p-4"> Empty shopping bag</span>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, toRef, watch } from "vue";
import { TrashIcon } from "@heroicons/vue/24/outline";
import type { Basket } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";
import SharedCheckbox from "../SharedCheckbox.vue";
import { formatPriceWithSymbol } from "@/utils/currencyConfig";

interface Props {
	basket: Basket;
	orderId: string;
}

const props = defineProps<Props>();

const hasDiscountCode = ref(false);
const discountCode = ref<string | null>(null);
const basketRef = toRef(() => props.basket);
const itemsRef = ref(basketRef.value.Items);

const norceStore = useNorceStore();
const cartStore = useCartStore();

const handleDiscountCode = (action: "apply" | "delete") => {
	cartStore.updateBasket({
		Id: basketRef.value.Id,
		OrderReference: props.orderId,
		DiscountCode: action === "apply" ? discountCode.value : null
	});

	if (action === "delete") {
		discountCode.value = null;
	}
};

const appliedPromotion = computed(
	() => basketRef.value?.AppliedPromotions || []
);

const originalSubtotal = computed(() => {
	const subtotal = basketRef.value?.Summary?.Items.AmountIncVat || 0;
	const discounts = appliedPromotion.value.reduce(
		(sum, p) => sum + (p.AppliedAmountIncVat || 0),
		0
	);
	return subtotal + discounts;
});

onMounted(() => {
	if (basketRef.value.DiscountCode) {
		discountCode.value = basketRef.value.DiscountCode;
		hasDiscountCode.value = true;
	}
});

const formatPrice = (amount: number | undefined) =>
	formatPriceWithSymbol(amount ?? 0, norceStore?.currentCurrency?.Code);

watch(basketRef, () => {
	itemsRef.value = basketRef.value.Items;
});
</script>
